/**
 * product-new.js
 *
 * Scoped behaviours for the new blend product template:
 *   - Image gallery (prev/next + thumb sync)
 *   - Jump-link scroll spy
 *   - Sticky add-to-cart bar (mobile)
 *   - Custom variant dropdown (grind): light typography via button + list, hidden native select
 *   - Add to cart label: line subtotal (unit × qty) — Add to cart
 *
 * Relies on nothing outside this file. Expects DOM hooks emitted by
 * sections/product-new-main.liquid.
 */
(function () {
  'use strict';

  if (window.__pnProductNewJsLoaded) return;
  window.__pnProductNewJsLoaded = true;

  /** While set, Judge.me scrollIntoView calls inside this subtree are ignored (pagination / sort / histogram filter). */
  var pnJdgmScrollLock = { root: null, until: 0 };
  var pnNativeScrollIntoView = Element.prototype.scrollIntoView;
  if (!Element.prototype.scrollIntoView.__pnWrapped) {
    Element.prototype.scrollIntoView = function () {
      if (
        pnJdgmScrollLock.root &&
        Date.now() < pnJdgmScrollLock.until &&
        pnJdgmScrollLock.root.contains(this)
      ) {
        return;
      }
      return pnNativeScrollIntoView.apply(this, arguments);
    };
    Element.prototype.scrollIntoView.__pnWrapped = true;
  }

  function pnBumpJdgmScrollLock(section, ms) {
    var t = Date.now() + ms;
    pnJdgmScrollLock.root = section;
    if (t > pnJdgmScrollLock.until) pnJdgmScrollLock.until = t;
  }

  /** Judge.me empties .jdgm-rev-widg__body while fetching; hold min-height so the block does not collapse. */
  function pnLockJdgmReviewListHeight(section) {
    var body = section.querySelector('.jdgm-rev-widg__body');
    if (!body) return;
    var h = body.offsetHeight;
    if (h < 72) return;
    body.style.minHeight = h + 'px';
    body.setAttribute('data-pn-jdgm-list-minh', '1');
  }

  function pnReleaseJdgmReviewListHeight(section) {
    var body = section.querySelector('.jdgm-rev-widg__body');
    if (!body || body.getAttribute('data-pn-jdgm-list-minh') !== '1') return;
    body.style.minHeight = '';
    body.removeAttribute('data-pn-jdgm-list-minh');
  }

  function pnGetVariantFromDom(root) {
    var master = root.querySelector('[data-product-select]');
    var vj = root.querySelector('[data-variant-json]');
    if (!master || !vj) return null;
    try {
      var variants = JSON.parse(vj.textContent);
      var id = master.value;
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(id)) return variants[i];
      }
    } catch (err) {
      /* ignore */
    }
    return null;
  }

  function pnGetQuantityFromRoot(root) {
    var inp = root.querySelector('input[name="quantity"].js-qty__num');
    if (!inp) inp = root.querySelector('input[name="quantity"]');
    var q = parseInt(inp && inp.value, 10);
    return !isNaN(q) && q > 0 ? q : 1;
  }

  function pnGetSubscribeWidgetData(root) {
    var jsonEl = root.querySelector('[data-pn-subscribe-widget-json]');
    if (!jsonEl) return null;
    try {
      return JSON.parse(jsonEl.textContent || '{}');
    } catch (err) {
      return null;
    }
  }

  function pnPlansForVariant(data, variantId) {
    if (!data || variantId == null) return [];
    var arr = data.plansByVariant[String(variantId)];
    if (!arr) arr = data.plansByVariant[variantId];
    return Array.isArray(arr) ? arr : [];
  }

  function pnFirstPlanIdForVariant(root, variant) {
    if (!variant) return null;
    var data = pnGetSubscribeWidgetData(root);
    var plans = pnPlansForVariant(data, variant.id);
    if (!plans.length) return null;
    var id = parseInt(plans[0].id, 10);
    return !isNaN(id) ? id : null;
  }

  function pnVariantRequiresSellingPlan(root, variant) {
    if (!variant) return false;
    if (variant.requires_selling_plan === true) return true;
    if (root && root.getAttribute('data-pn-subscription-required') === 'true') return true;
    var widget = root && root.querySelector('[data-pn-subscribe-widget]');
    if (widget && widget.getAttribute('data-pn-subscription-required') === 'true') return true;
    var data = pnGetSubscribeWidgetData(root);
    if (!data || !data.requiresSellingPlanByVariant) return false;
    var req = data.requiresSellingPlanByVariant[String(variant.id)];
    if (req == null) req = data.requiresSellingPlanByVariant[variant.id];
    return !!req;
  }

  function pnGetSellingPlanField(root) {
    var form = root && root.querySelector('form.product-single__form');
    if (!form) return null;

    var nativeSw = root.querySelector('[data-pn-sw-native]');
    if (nativeSw) return nativeSw;

    return form.querySelector('select[name="selling_plan"], input[name="selling_plan"]');
  }

  function pnEnsureSellingPlanField(root, planId) {
    if (!planId || !root) return null;
    var form = root.querySelector('form.product-single__form');
    if (!form) return null;
    var field = form.querySelector('select[name="selling_plan"], input[name="selling_plan"]');
    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = 'selling_plan';
      field.setAttribute('data-pn-selling-plan', '');
      form.insertBefore(field, form.firstChild);
    }
    field.disabled = false;
    field.value = String(planId);
    return field;
  }

  function pnMustAttachSellingPlan(root, variant) {
    if (!root) return false;
    if (root.getAttribute('data-pn-force-selling-plan') === 'true') return true;
    if (root.getAttribute('data-pn-subscription-required') === 'true') return true;
    if (root.querySelector('[data-pn-selling-plan-fields]')) return true;
    if (pnVariantRequiresSellingPlan(root, variant)) return true;
    var toggle = root.querySelector('[data-pn-sw-toggle]');
    if (toggle && toggle.classList.contains('is-on')) return true;
    return false;
  }

  /** Read the live selling_plan value from the product form (not cached JSON / first plan). */
  function pnGetSelectedSellingPlanId(root) {
    if (!root) return null;

    var toggle = root.querySelector('[data-pn-sw-toggle]');
    var subscribeOn = toggle && toggle.classList.contains('is-on');

    if (toggle && !subscribeOn) {
      return null;
    }

    var nativeSw = root.querySelector('[data-pn-sw-native]');
    if (nativeSw && subscribeOn) {
      var swVal = String(nativeSw.value || '').trim();
      if (swVal) {
        var swId = parseInt(swVal, 10);
        if (!isNaN(swId)) return swId;
      }
    }

    var field = pnGetSellingPlanField(root);
    if (field && !field.disabled) {
      var val = String(field.value || '').trim();
      if (val) {
        var id = parseInt(val, 10);
        if (!isNaN(id)) return id;
      }
    }

    return null;
  }

  function pnGetSellingPlanIdFromRoot(root, variant) {
    variant = variant || pnGetVariantFromDom(root);

    var selected = pnGetSelectedSellingPlanId(root);
    if (selected) return selected;

    if (pnMustAttachSellingPlan(root, variant)) {
      var forced = pnFirstPlanIdForVariant(root, variant);
      if (forced) return forced;
    }

    return null;
  }

  function pnEnsureSellingPlanBeforeAdd(root, variant) {
    variant = variant || pnGetVariantFromDom(root);
    var planId = pnGetSellingPlanIdFromRoot(root, variant);

    var field = pnGetSellingPlanField(root);
    if (field) {
      if (planId) {
        field.disabled = false;
        field.value = String(planId);
      } else if (!pnMustAttachSellingPlan(root, variant)) {
        field.disabled = true;
        field.value = '';
      }
    } else if (planId) {
      pnEnsureSellingPlanField(root, planId);
    }

    return planId;
  }

  function initSellingPlanOnAddToCart(root) {
    var form = root.querySelector('form.product-single__form');
    if (!form || form.dataset.pnSellingPlanGuard === '1') return;
    form.dataset.pnSellingPlanGuard = '1';

    form.addEventListener(
      'submit',
      function () {
        pnEnsureSellingPlanBeforeAdd(root);
      },
      true
    );
  }

  function pnGetSubscribeUnitCents(root, variant) {
    var widget = root.querySelector('[data-pn-subscribe-widget]');
    if (!widget || widget.hidden) return null;
    var native = widget.querySelector('[data-pn-sw-native]');
    var toggle = widget.querySelector('[data-pn-sw-toggle]');
    if (!native || !toggle || !toggle.classList.contains('is-on')) return null;
    if (!native.value) return null;
    var jsonEl = root.querySelector('[data-pn-subscribe-widget-json]');
    if (!jsonEl) return null;
    try {
      var data = JSON.parse(jsonEl.textContent);
      var plans = data.plansByVariant[String(variant.id)];
      if (!plans) plans = data.plansByVariant[variant.id];
      if (!Array.isArray(plans)) return null;
      var plan = plans.filter(function (p) {
        return String(p.id) === String(native.value);
      })[0];
      return plan ? plan.price : null;
    } catch (e) {
      return null;
    }
  }

  function pnGetUnitCents(root, variant) {
    if (!variant) return 0;
    var sub = pnGetSubscribeUnitCents(root, variant);
    if (sub != null) return sub;
    return variant.price;
  }

  function pnDispatchSubscribePricing(root) {
    root.dispatchEvent(new CustomEvent('pn:subscribe-pricing'));
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /**
   * FAQ accordions on product-new-faq: intercept native <details> toggle.
   * Open: opacity fade only (no max-height → auto handoff = no end “jump”).
   * Close: max-height + opacity (unchanged).
   */
  function initPdpFaqAccordions(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var containers = root.querySelectorAll('.product-new__accordion--pdp-faq');
    if (!containers.length) return;

    var reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    containers.forEach(function (container) {
      if (container.getAttribute('data-pn-faq-accordion-init') === '1') return;
      container.setAttribute('data-pn-faq-accordion-init', '1');

      container.querySelectorAll('details').forEach(function (details) {
        var summary = details.querySelector('summary');
        var body = details.querySelector('.product-new__accordion-body');
        if (!summary || !body) return;

        var closeOnEnd = null;
        var openOnEnd = null;

        function clearCloseListener() {
          if (closeOnEnd) {
            body.removeEventListener('transitionend', closeOnEnd);
            closeOnEnd = null;
          }
        }

        function clearOpenListener() {
          if (openOnEnd) {
            body.removeEventListener('transitionend', openOnEnd);
            openOnEnd = null;
          }
        }

        function syncAria() {
          summary.setAttribute(
            'aria-expanded',
            details.hasAttribute('open') ? 'true' : 'false'
          );
        }

        var closeFallbackTimer = null;
        var openFallbackTimer = null;

        function clearCloseFallback() {
          if (closeFallbackTimer) {
            clearTimeout(closeFallbackTimer);
            closeFallbackTimer = null;
          }
        }

        function clearOpenFallback() {
          if (openFallbackTimer) {
            clearTimeout(openFallbackTimer);
            openFallbackTimer = null;
          }
        }

        function finishClose() {
          clearCloseListener();
          clearCloseFallback();
          clearOpenListener();
          clearOpenFallback();
          details.removeAttribute('open');
          body.classList.remove('pn-faq-body--closing');
          body.style.maxHeight = '';
          body.style.overflow = '';
          body.style.opacity = '';
          syncAria();
        }

        function openPanel() {
          clearCloseListener();
          clearCloseFallback();
          clearOpenListener();
          clearOpenFallback();

          body.classList.remove('pn-faq-body--closing');
          body.style.maxHeight = '';
          body.style.overflow = '';
          body.style.opacity = '';

          details.setAttribute('open', '');
          syncAria();

          if (reduceMotion) {
            return;
          }

          /* Fade answer in only — layout uses natural height (avoids max-height / auto swap glitch). */
          body.style.opacity = '0';
          void body.offsetHeight;

          var opened = false;
          openOnEnd = function (ev) {
            if (ev.target !== body || ev.propertyName !== 'opacity') return;
            if (opened) return;
            opened = true;
            clearOpenListener();
            clearOpenFallback();
            body.style.opacity = '';
          };
          body.addEventListener('transitionend', openOnEnd, false);

          openFallbackTimer = window.setTimeout(function () {
            openFallbackTimer = null;
            if (!details.hasAttribute('open')) return;
            if (opened) return;
            opened = true;
            clearOpenListener();
            body.style.opacity = '';
          }, 550);

          window.requestAnimationFrame(function () {
            body.style.opacity = '1';
          });
        }

        function closePanel() {
          if (reduceMotion) {
            finishClose();
            return;
          }
          clearCloseListener();
          clearCloseFallback();
          clearOpenListener();
          clearOpenFallback();
          var closed = false;
          closeOnEnd = function (ev) {
            if (ev.target !== body || ev.propertyName !== 'max-height') return;
            if (closed) return;
            closed = true;
            clearCloseListener();
            clearCloseFallback();
            finishClose();
          };
          body.addEventListener('transitionend', closeOnEnd, false);
          var h = body.scrollHeight;
          body.style.overflow = 'hidden';
          body.style.maxHeight = h + 'px';
          void body.offsetHeight;
          window.requestAnimationFrame(function () {
            body.classList.add('pn-faq-body--closing');
            void body.offsetHeight;
            window.requestAnimationFrame(function () {
              body.style.maxHeight = '0';
              body.style.opacity = '0';
            });
          });
          closeFallbackTimer = window.setTimeout(function () {
            closeFallbackTimer = null;
            if (!details.hasAttribute('open')) return;
            if (closed) return;
            closed = true;
            clearCloseListener();
            finishClose();
          }, 650);
        }

        function onActivate(e) {
          if (e) e.preventDefault();
          if (details.hasAttribute('open')) {
            if (closeOnEnd) {
              clearCloseListener();
              clearCloseFallback();
              body.classList.remove('pn-faq-body--closing');
              body.style.maxHeight = '';
              body.style.overflow = '';
              body.style.opacity = '1';
              syncAria();
              return;
            }
            if (openOnEnd) {
              clearOpenListener();
              clearOpenFallback();
              body.classList.remove('pn-faq-body--closing');
              body.style.maxHeight = '';
              body.style.overflow = '';
              body.style.opacity = '';
              syncAria();
              closePanel();
              return;
            }
            closePanel();
          } else {
            openPanel();
          }
        }

        syncAria();

        summary.addEventListener('click', onActivate);
        summary.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onActivate();
          }
        });
      });
    });
  }

  function initGallery(root) {
    var gallery = root.querySelector('[data-pn-gallery]');
    if (!gallery) return;

    var slides = gallery.querySelectorAll('[data-pn-slide]');
    if (!slides.length) return;

    var variantMediaMap = {};
    var mapEl = gallery.querySelector('[data-pn-variant-media-map]');
    if (mapEl) {
      try {
        variantMediaMap = JSON.parse(mapEl.textContent || '{}');
      } catch (e) {
        variantMediaMap = {};
      }
    }

    var index = 0;

    function show(i) {
      index = (i + slides.length) % slides.length;
      slides.forEach(function (slide, idx) {
        slide.classList.toggle('is-active', idx === index);
      });
      gallery.querySelectorAll('[data-pn-thumb]').forEach(function (thumb, idx) {
        thumb.classList.toggle('is-active', idx === index);
      });
    }

    function slideIndexForVariant(variant) {
      if (!variant) return -1;

      var entry = variantMediaMap[String(variant.id)] || null;
      var mediaId = entry && entry.mediaId != null ? entry.mediaId : null;
      var imageId = entry && entry.imageId != null ? entry.imageId : null;

      if (!mediaId && variant.featured_media && variant.featured_media.id != null) {
        mediaId = variant.featured_media.id;
      }
      if (!imageId && variant.featured_image && variant.featured_image.id != null) {
        imageId = variant.featured_image.id;
      }

      if (mediaId == null && imageId == null) return -1;

      for (var i = 0; i < slides.length; i++) {
        var slide = slides[i];
        if (mediaId != null && String(slide.getAttribute('data-pn-media-id')) === String(mediaId)) {
          return i;
        }
        if (imageId != null && String(slide.getAttribute('data-pn-image-id')) === String(imageId)) {
          return i;
        }
      }
      return -1;
    }

    function showForVariant(variant) {
      var idx = slideIndexForVariant(variant);
      if (idx !== -1) show(idx);
    }

    function onVariantUpdate(ev) {
      showForVariant(ev.detail && ev.detail.variant);
    }

    root.addEventListener('variantChange', onVariantUpdate);
    root.addEventListener('variantImageChange', onVariantUpdate);

    gallery.addEventListener('click', function (e) {
      var el = e.target;
      if (!el || !el.closest) return;

      if (el.closest('[data-pn-prev]')) {
        e.preventDefault();
        show(index - 1);
        return;
      }
      if (el.closest('[data-pn-next]')) {
        e.preventDefault();
        show(index + 1);
        return;
      }
      var thumb = el.closest('[data-pn-thumb]');
      if (thumb) {
        var thumbs = gallery.querySelectorAll('[data-pn-thumb]');
        var idx = Array.prototype.indexOf.call(thumbs, thumb);
        if (idx !== -1) show(idx);
      }
    });

    show(0);

    var initialVariant = pnGetVariantFromDom(root);
    if (initialVariant) {
      showForVariant(initialVariant);
    }
  }

  function initJumpLinks(root) {
    var links = root.querySelectorAll('[data-pn-jumplink]');
    if (!links.length) return;

    var targets = [];
    links.forEach(function (link) {
      var id = link.getAttribute('data-pn-jumplink');
      var el = document.getElementById(id);
      if (!el) {
        link.hidden = true;
        return;
      }
      targets.push({ link: link, el: el, id: id });
    });

    links.forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        var id = link.getAttribute('data-pn-jumplink');
        var target = document.getElementById(id);
        if (!target) return;
        var top = target.getBoundingClientRect().top + window.pageYOffset - 80;
        window.scrollTo({ top: top, behavior: 'smooth' });
      });
    });

    function onScroll() {
      var scroll = window.pageYOffset + 120;
      var current = null;
      for (var i = 0; i < targets.length; i++) {
        var t = targets[i];
        var top = t.el.offsetTop;
        var bottom = top + t.el.offsetHeight;
        if (scroll >= top && scroll < bottom) {
          current = t.id;
          break;
        }
      }
      links.forEach(function (link) {
        link.classList.toggle(
          'is-active',
          link.getAttribute('data-pn-jumplink') === current
        );
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  function initHeroRatingScroll(root) {
    var link = root.querySelector('[data-pn-rating-scroll]');
    if (!link) return;

    link.addEventListener('click', function (e) {
      var hash = link.getAttribute('href');
      if (!hash || hash.charAt(0) !== '#') return;
      var id = decodeURIComponent(hash.slice(1));
      if (!id) return;
      var target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      var top = target.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top: top, behavior: 'smooth' });
      try {
        history.replaceState(null, '', hash);
      } catch (err) {
        /* ignore */
      }
    });
  }

  function initSubscribeSavePromo(root) {
    var wrap = root.querySelector('[data-pn-subscribe-save]');
    var pctEl = root.querySelector('[data-pn-subscribe-save-pct]');
    var jsonEl = root.querySelector('[data-pn-subscribe-save-json]');
    if (!wrap || !pctEl || !jsonEl) return;

    var map = {};
    try {
      map = JSON.parse(jsonEl.textContent || '{}');
    } catch (e) {
      return;
    }

    function applyPct(variantId) {
      if (variantId == null) {
        wrap.hidden = true;
        return;
      }
      var raw = map[String(variantId)];
      var n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
      if (!n || n <= 0 || isNaN(n)) {
        wrap.hidden = true;
        return;
      }
      wrap.hidden = false;
      pctEl.textContent = 'Save ' + Math.round(n) + '%';
    }

    root.addEventListener('variantChange', function (ev) {
      var v = ev.detail && ev.detail.variant;
      applyPct(v ? v.id : null);
    });

    var sel = root.querySelector('[data-product-select]');
    if (sel && sel.value) {
      applyPct(sel.value);
    }
  }

  function initSubscribeWidget(root) {
    var widget = root.querySelector('[data-pn-subscribe-widget]');
    var jsonEl = root.querySelector('[data-pn-subscribe-widget-json]');
    if (!widget || !jsonEl) return;

    var data;
    try {
      data = JSON.parse(jsonEl.textContent || '{}');
    } catch (e) {
      return;
    }

    var toggle = widget.querySelector('[data-pn-sw-toggle]');
    var body = widget.querySelector('[data-pn-sw-body]');
    var pctEl = widget.querySelector('[data-pn-sw-pct]');
    var descOn = widget.querySelector('[data-pn-sw-desc-on]');
    var descOff = widget.querySelector('[data-pn-sw-desc-off]');
    var native = widget.querySelector('[data-pn-sw-native]');
    var btn = widget.querySelector('[data-pn-sw-trigger]');
    var list = widget.querySelector('[data-pn-sw-list]');
    var valueEl = widget.querySelector('[data-pn-sw-value]');
    var outer = widget.querySelector('.variant-wrapper--dropdown-custom');
    var currentVariant = null;
    var swOn = false;

    function plansForVariant(vid) {
      var arr = data.plansByVariant[String(vid)];
      if (!arr) arr = data.plansByVariant[vid];
      return Array.isArray(arr) ? arr : [];
    }

    function moneyFmt() {
      return (
        data.moneyFormat ||
        (window.theme && theme.settings && theme.settings.moneyFormat) ||
        '${{amount}}'
      );
    }

    function setToggleUi(on) {
      swOn = on;
      toggle.setAttribute('aria-checked', on ? 'true' : 'false');
      toggle.classList.toggle('is-on', on);
      body.classList.toggle('hide', !on);
      descOff.classList.toggle('hide', on);
      descOn.classList.toggle('hide', !on);
      if (on) startSubscribePriceGuard();
      else stopSubscribePriceGuard();
    }

    function rebuildPlanSelect(variantId) {
      var plans = plansForVariant(variantId);
      while (native.options.length) {
        native.remove(0);
      }
      var empty = document.createElement('option');
      empty.value = '';
      native.add(empty);
      plans.forEach(function (p) {
        var o = document.createElement('option');
        o.value = String(p.id);
        o.textContent = p.label;
        native.add(o);
      });

      list.innerHTML = '';
      plans.forEach(function (p) {
        var li = document.createElement('li');
        li.setAttribute('role', 'option');
        li.className = 'product-new__custom-select-option';
        li.textContent = p.label;
        list.appendChild(li);
      });
    }

    function syncCustomListFromNative() {
      var opt = native.options[native.selectedIndex];
      var label = opt && opt.value ? opt.textContent.trim() : '';
      if (valueEl) valueEl.textContent = label;
      var items = list.querySelectorAll('.product-new__custom-select-option');
      Array.prototype.forEach.call(items, function (li, i) {
        var optEl = native.options[i + 1];
        var sel = !!(optEl && optEl.selected);
        li.setAttribute('aria-selected', sel ? 'true' : 'false');
        li.classList.toggle('is-selected', sel);
      });
    }

    function setListOpen(isOpen) {
      if (outer) outer.classList.toggle('is-open', isOpen);
      list.hidden = !isOpen;
      btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }

    function updateDescOn() {
      var opt = native.options[native.selectedIndex];
      if (!descOn) return;
      descOn.textContent = opt && opt.value ? opt.textContent.trim() : '';
    }

    var pnRechargePriceBeatTimer = null;
    var pnSubscribePriceMo = null;
    var pnSubscribePriceGuardPending = false;

    function getSubscribePriceLabel() {
      if (!currentVariant || !window.theme || !theme.Currency) return null;
      var sid = native.value;
      if (!sid) return null;
      var plans = plansForVariant(currentVariant.id);
      var plan = plans.filter(function (p) {
        return String(p.id) === String(sid);
      })[0];
      if (!plan) return null;
      return theme.Currency.formatMoney(plan.price, moneyFmt()).trim();
    }

    function guardSubscribeHeroPrice() {
      if (!swOn) return;
      var priceEl = root.querySelector('[data-product-price]');
      var expected = getSubscribePriceLabel();
      if (!priceEl || !expected) return;
      if (priceEl.textContent.trim() === expected) return;
      updatePriceDisplay(true, true);
    }

    function stopSubscribePriceGuard() {
      if (pnSubscribePriceMo) {
        pnSubscribePriceMo.disconnect();
        pnSubscribePriceMo = null;
      }
      pnSubscribePriceGuardPending = false;
      if (pnRechargePriceBeatTimer) {
        window.clearTimeout(pnRechargePriceBeatTimer);
        pnRechargePriceBeatTimer = null;
      }
    }

    function startSubscribePriceGuard() {
      var priceEl = root.querySelector('[data-product-price]');
      if (!priceEl) return;
      stopSubscribePriceGuard();
      pnSubscribePriceMo = new MutationObserver(function () {
        if (!swOn || pnSubscribePriceGuardPending) return;
        pnSubscribePriceGuardPending = true;
        window.requestAnimationFrame(function () {
          guardSubscribeHeroPrice();
          pnSubscribePriceGuardPending = false;
        });
      });
      pnSubscribePriceMo.observe(priceEl, { childList: true, subtree: true, characterData: true });
    }

    /** Recharge widget 2.0 async-overwrites [data-product-price] with one-time variant price. */
    function scheduleSubscribePriceBeat(subscribeOn) {
      if (!subscribeOn) return;
      startSubscribePriceGuard();
      function apply() {
        guardSubscribeHeroPrice();
      }
      apply();
      window.requestAnimationFrame(apply);
      if (pnRechargePriceBeatTimer) window.clearTimeout(pnRechargePriceBeatTimer);
      [0, 50, 100, 200, 400, 800, 1500].forEach(function (ms) {
        window.setTimeout(apply, ms);
      });
      pnRechargePriceBeatTimer = window.setTimeout(apply, 2500);
    }

    function updatePriceDisplay(subscribeOn, skipRechargeBeat) {
      var on = subscribeOn != null ? !!subscribeOn : swOn;
      var priceEl = root.querySelector('[data-product-price]');
      var wrap = root.querySelector('[data-product-price-wrap]');
      var compareEl = root.querySelector('[data-compare-price]');
      var priceMain = root.querySelector('.product-new__price:not(.product-new__price--compare)');
      if (!currentVariant || !priceEl || !window.theme || !theme.Currency) return;

      var fmt = moneyFmt();

      if (!on) {
        priceEl.innerHTML = theme.Currency.formatMoney(currentVariant.price, fmt);
        if (currentVariant.compare_at_price > currentVariant.price) {
          if (compareEl) compareEl.innerHTML = theme.Currency.formatMoney(currentVariant.compare_at_price, fmt);
          if (wrap) wrap.classList.remove('hide');
          if (priceMain) priceMain.classList.add('sale-price');
        } else {
          if (wrap) wrap.classList.add('hide');
          if (priceMain) priceMain.classList.remove('sale-price');
        }
        pnDispatchSubscribePricing(root);
        return;
      }

      var sid = native.value;
      var plans = plansForVariant(currentVariant.id);
      var plan = plans.filter(function (p) {
        return String(p.id) === String(sid);
      })[0];
      if (!plan) {
        pnDispatchSubscribePricing(root);
        return;
      }

      priceEl.innerHTML = theme.Currency.formatMoney(plan.price, fmt);
      var cmp = plan.compare_at_price > plan.price ? plan.compare_at_price : 0;
      if (!cmp && currentVariant.compare_at_price > plan.price) {
        cmp = currentVariant.compare_at_price;
      }
      if (cmp > plan.price) {
        if (compareEl) compareEl.innerHTML = theme.Currency.formatMoney(cmp, fmt);
        if (wrap) wrap.classList.remove('hide');
        if (priceMain) priceMain.classList.add('sale-price');
      } else {
        if (wrap) wrap.classList.add('hide');
        if (priceMain) priceMain.classList.remove('sale-price');
      }
      pnDispatchSubscribePricing(root);
      if (!skipRechargeBeat) scheduleSubscribePriceBeat(true);
    }

    function variantRequiresPlan(v) {
      return pnVariantRequiresSellingPlan(root, v);
    }

    function syncToVariant(v) {
      currentVariant = v;
      if (!v) return;

      var plans = plansForVariant(v.id);
      if (plans.length === 0) {
        widget.hidden = true;
        native.selectedIndex = 0;
        setToggleUi(false);
        setListOpen(false);
        updatePriceDisplay();
        return;
      }
      widget.hidden = false;

      var pct = data.pctByVariant[String(v.id)];
      if (pct == null) pct = data.pctByVariant[v.id];
      if (pctEl && pct != null) pctEl.textContent = String(Math.round(pct));

      rebuildPlanSelect(v.id);
      var mustSubscribe = variantRequiresPlan(v);
      var subscribeOn = false;
      if (mustSubscribe || swOn) {
        subscribeOn = true;
        setToggleUi(true);
        native.selectedIndex = native.options.length > 1 ? 1 : 0;
      } else {
        setToggleUi(false);
        native.selectedIndex = 0;
      }
      native.dispatchEvent(new Event('change', { bubbles: true }));
      syncCustomListFromNative();
      updateDescOn();
      updatePriceDisplay(subscribeOn);
      setListOpen(false);
    }

    function debugEnabled() {
      return widget.hasAttribute('data-pn-sw-debug') || root.hasAttribute('data-pn-sw-debug');
    }

    /**
     * Resolve current variant lazily. Does NOT call syncToVariant (which can
     * hide the widget if the resolved variant has no plans). Sets currentVariant
     * directly so the toggle keeps working even if the initial DOM read failed.
     */
    function resolveVariantForClick() {
      if (currentVariant) return currentVariant;
      var resolved = pnGetVariantFromDom(root);
      if (resolved && plansForVariant(resolved.id).length > 0) {
        currentVariant = resolved;
        rebuildPlanSelect(resolved.id);
        var pct = data.pctByVariant && (data.pctByVariant[String(resolved.id)] != null ? data.pctByVariant[String(resolved.id)] : data.pctByVariant[resolved.id]);
        if (pctEl && pct != null) pctEl.textContent = String(Math.round(pct));
        return currentVariant;
      }
      var keys = Object.keys(data.plansByVariant || {});
      for (var k = 0; k < keys.length; k++) {
        var arr = data.plansByVariant[keys[k]];
        if (Array.isArray(arr) && arr.length > 0) {
          currentVariant = resolved || { id: keys[k] };
          rebuildPlanSelect(keys[k]);
          var p = data.pctByVariant && data.pctByVariant[keys[k]];
          if (pctEl && p != null) pctEl.textContent = String(Math.round(p));
          return currentVariant;
        }
      }
      return null;
    }

    toggle.addEventListener('click', function () {
      var v = resolveVariantForClick();
      if (!v) {
        if (debugEnabled()) console.debug('[pn-sw] toggle ignored: no current variant');
        return;
      }
      if (plansForVariant(v.id).length === 0) {
        if (debugEnabled()) console.debug('[pn-sw] toggle ignored: no plans for variant', v.id);
        return;
      }
      if (native.options.length <= 1) {
        rebuildPlanSelect(v.id);
      }
      var next = !swOn;
      if (!next && variantRequiresPlan(v)) {
        return;
      }
      setToggleUi(next);
      if (next) {
        if (native.options.length > 1) native.selectedIndex = 1;
      } else {
        native.selectedIndex = 0;
      }
      native.dispatchEvent(new Event('change', { bubbles: true }));
      syncCustomListFromNative();
      updateDescOn();
      updatePriceDisplay(next);
      if (!next) setListOpen(false);
    });

    native.addEventListener('change', function () {
      syncCustomListFromNative();
      updateDescOn();
      if (swOn) {
        updatePriceDisplay(true);
      } else {
        pnDispatchSubscribePricing(root);
      }
    });

    root.addEventListener('variantPriceChange', function () {
      if (swOn) scheduleSubscribePriceBeat(true);
    });

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (!swOn) return;
      var opening = list.hidden;
      if (opening && outer) closeOtherPnCustomSelects(outer);
      setListOpen(opening);
    });

    btn.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && outer && outer.classList.contains('is-open')) {
        setListOpen(false);
      }
    });

    list.addEventListener('click', function (e) {
      var li = e.target.closest('.product-new__custom-select-option');
      if (!li) return;
      var items = list.querySelectorAll('.product-new__custom-select-option');
      var idx = Array.prototype.indexOf.call(items, li);
      if (idx < 0) return;
      var optIdx = idx + 1;
      if (!native.options[optIdx]) return;
      native.selectedIndex = optIdx;
      native.dispatchEvent(new Event('change', { bubbles: true }));
      setListOpen(false);
      syncCustomListFromNative();
    });

    bindPnCustomSelectOutsideClose();

    root.addEventListener('variantChange', function (ev) {
      var v = ev.detail && ev.detail.variant;
      syncToVariant(v);
    });

    var initial = pnGetVariantFromDom(root);
    if (initial) {
      syncToVariant(initial);
    } else {
      window.requestAnimationFrame(function () {
        var v = pnGetVariantFromDom(root);
        if (v) syncToVariant(v);
      });
      window.setTimeout(function () {
        if (!currentVariant) {
          var v = pnGetVariantFromDom(root);
          if (v) syncToVariant(v);
        }
      }, 250);
    }

    /* One-time init signal so you can confirm wiring in DevTools.
       To get verbose toggle logs, add `data-pn-sw-debug` to the widget div. */
    try {
      var planCount = 0;
      var keys = Object.keys(data.plansByVariant || {});
      for (var i = 0; i < keys.length; i++) {
        if (Array.isArray(data.plansByVariant[keys[i]])) planCount += data.plansByVariant[keys[i]].length;
      }
      console.debug(
        '[pn-sw] init',
        'variants=' + keys.length,
        'totalPlans=' + planCount,
        'currentVariantResolved=' + !!currentVariant
      );
    } catch (_) { /* ignore */ }
  }

  /** Sync hidden selling_plan field when the visible Subscribe widget is off. */
  function initHiddenSellingPlanFields(root) {
    var fields = root.querySelector('[data-pn-selling-plan-fields]');
    var jsonEl = root.querySelector('[data-pn-subscribe-widget-json]');
    if (!fields || !jsonEl) return;
    if (root.querySelector('[data-pn-sw-toggle]')) return;

    function syncPlanField(variant) {
      if (!variant) return;
      var planId = pnFirstPlanIdForVariant(root, variant);
      if (planId) pnEnsureSellingPlanField(root, planId);
    }

    root.addEventListener('variantChange', function (ev) {
      syncPlanField(ev.detail && ev.detail.variant);
    });

    syncPlanField(pnGetVariantFromDom(root));
  }

  function initAddToCartSubtotal(root) {
    var btn = root.querySelector('[data-add-to-cart]');
    var line = root.querySelector('[data-pn-atc-line]');
    var subEl = root.querySelector('[data-pn-atc-subtotal]');
    var textEl = root.querySelector('[data-add-to-cart-text]');
    if (!btn || !line || !subEl || !textEl) return;

    var lastVariant = null;
    var stickyBtn = root.querySelector('[data-pn-sticky-btn]');

    function moneyFmt() {
      return window.theme && theme.settings && theme.settings.moneyFormat
        ? theme.settings.moneyFormat
        : '${{amount}}';
    }

    function getQty() {
      return pnGetQuantityFromRoot(root);
    }

    function syncStickyBtn(subtotalStr, actionText) {
      if (!stickyBtn || !btn) return;
      stickyBtn.disabled = btn.disabled;
      if (!lastVariant || !lastVariant.available) {
        stickyBtn.textContent = stickyBtn.getAttribute('data-soldout-text') || 'Sold out';
        return;
      }
      stickyBtn.textContent = subtotalStr + '\u2009\u2013\u2009' + actionText;
    }

    function refresh() {
      var v = lastVariant;
      if (!v || !v.available) {
        line.setAttribute('hidden', '');
        subEl.textContent = '';
        syncStickyBtn('', '');
        return;
      }
      line.removeAttribute('hidden');
      if (!window.theme || !theme.Currency) return;

      var unit = pnGetUnitCents(root, v);
      var total = unit * getQty();
      var fmt = moneyFmt();
      var subtotalStr = theme.Currency.formatMoney(total, fmt);
      subEl.textContent = subtotalStr;
      var actionText = textEl.getAttribute('data-default-text') || 'Add to cart';
      syncStickyBtn(subtotalStr, actionText);
    }

    root.addEventListener('variantChange', function (ev) {
      lastVariant = ev.detail && ev.detail.variant;
      window.requestAnimationFrame(refresh);
    });

    root.addEventListener('pn:subscribe-pricing', function () {
      refresh();
    });

    /* Delegate on section root: quantity UI is often outside <form> (grind + qty row). */
    root.addEventListener('input', function (e) {
      if (e.target && e.target.name === 'quantity') refresh();
    });
    root.addEventListener('change', function (e) {
      if (e.target && e.target.name === 'quantity') refresh();
    });
    root.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('.js-qty__adjust')) {
        window.setTimeout(refresh, 0);
      }
    });

    lastVariant = pnGetVariantFromDom(root);
    refresh();
  }

  /**
   * Visible “More payment options” control (styled link). Adds current line to cart and
   * sends the customer to /checkout. Shop Pay row visibility is CSS + data-pn-show-shop-pay.
   */
  function initMorePaymentOptionsProxy(root) {
    var wrap = root.querySelector('[data-pn-dynamic-checkout]');
    var proxy = root.querySelector('[data-pn-more-payment-proxy]');
    if (!wrap || !proxy) return;

    function veilNativeMoreOptions() {
      wrap.querySelectorAll('.shopify-payment-button .shopify-payment-button__more-options').forEach(function (el) {
        if (el.hasAttribute('data-pn-more-payment-proxy')) return;
        el.setAttribute('aria-hidden', 'true');
        el.setAttribute('tabindex', '-1');
      });
    }

    proxy.addEventListener('click', function (e) {
      e.preventDefault();
      var atc = root.querySelector('[data-add-to-cart]');
      if (atc && atc.disabled) return;
      var variant = pnGetVariantFromDom(root);
      if (!variant || !variant.available) return;
      if (proxy.dataset.pnCheckoutBusy === '1') return;
      var qty = pnGetQuantityFromRoot(root);
      var item = { id: variant.id, quantity: qty };
      pnEnsureSellingPlanBeforeAdd(root, variant);
      var planId = pnGetSellingPlanIdFromRoot(root, variant);
      if (planId) item.selling_plan = planId;

      proxy.dataset.pnCheckoutBusy = '1';
      proxy.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] }),
        credentials: 'same-origin'
      })
        .then(function (res) {
          return res.json().then(function (body) {
            if (!res.ok) throw new Error((body && body.description) || body.message || res.statusText);
            return body;
          });
        })
        .then(function () {
          window.location.href = '/checkout';
        })
        .catch(function () {
          proxy.disabled = false;
          delete proxy.dataset.pnCheckoutBusy;
        });
    });

    veilNativeMoreOptions();

    var host = wrap.querySelector('.shopify-payment-button');
    if (!host || host.dataset.pnMorePaymentObs === '1') return;
    host.dataset.pnMorePaymentObs = '1';
    var mo = new MutationObserver(veilNativeMoreOptions);
    mo.observe(host, { childList: true, subtree: true });
  }

  function initStickyATC(root) {
    var bar = root.querySelector('[data-pn-sticky]');
    var trigger = root.querySelector('[data-pn-sticky-trigger]');
    if (!bar || !trigger) return;

    var mainAtc = root.querySelector('[data-add-to-cart]');
    var stickyBtn = bar.querySelector('[data-pn-sticky-btn]');
    var stickyVariant = bar.querySelector('[data-pn-sticky-variant]');

    function setStickyVariantData(v) {
      if (!stickyVariant) return;
      if (!v) {
        stickyVariant.setAttribute('hidden', '');
        stickyVariant.textContent = '';
        return;
      }
      var t = (v.title || '').trim();
      if (!t || t === 'Default Title') {
        stickyVariant.setAttribute('hidden', '');
        stickyVariant.textContent = '';
        return;
      }
      stickyVariant.removeAttribute('hidden');
      stickyVariant.textContent = t;
    }

    function syncStickyVariantFromEvent(ev) {
      if (ev && ev.detail) {
        setStickyVariantData(ev.detail.variant);
        return;
      }
      setStickyVariantData(pnGetVariantFromDom(root));
    }

    function syncPrice() {
      if (stickyBtn && mainAtc) {
        stickyBtn.disabled = mainAtc.disabled;
        if (root.querySelector('[data-pn-atc-subtotal]')) {
          return;
        }
        stickyBtn.textContent = mainAtc.disabled
          ? (stickyBtn.getAttribute('data-soldout-text') || 'Sold out')
          : (stickyBtn.getAttribute('data-default-text') || 'Add to cart');
      }
    }

    if (stickyBtn && mainAtc) {
      stickyBtn.addEventListener('click', function () {
        if (mainAtc.disabled) return;
        mainAtc.click();
      });
    }

    var stickyTitleJump = bar.querySelector('[data-pn-sticky-title-jump]');
    if (stickyTitleJump) {
      stickyTitleJump.addEventListener('click', function () {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        bar.classList.toggle('is-visible', !entry.isIntersecting);
      });
    }, { rootMargin: '0px 0px -80px 0px', threshold: 0 });

    observer.observe(trigger);

    if (mainAtc) {
      var atcObserver = new MutationObserver(syncPrice);
      atcObserver.observe(mainAtc, {
        attributes: true,
        attributeFilter: ['disabled'],
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    root.addEventListener('variantChange', function (ev) {
      syncStickyVariantFromEvent(ev);
    });
    setStickyVariantData(pnGetVariantFromDom(root));
    syncPrice();
  }

  var pnCustomDdOutsideBound = false;

  function pnDdListEl(outer) {
    return outer.querySelector('[data-pn-custom-select-list], [data-pn-sw-list]');
  }

  function pnDdTriggerEl(outer) {
    return outer.querySelector('[data-pn-custom-select-trigger], [data-pn-sw-trigger]');
  }

  function bindPnCustomSelectOutsideClose() {
    if (pnCustomDdOutsideBound) return;
    pnCustomDdOutsideBound = true;
    document.addEventListener('click', function (e) {
      document.querySelectorAll('.variant-wrapper--dropdown-custom.is-open').forEach(function (ow) {
        if (ow.contains(e.target)) return;
        ow.classList.remove('is-open');
        var l = pnDdListEl(ow);
        var b = pnDdTriggerEl(ow);
        if (l) l.hidden = true;
        if (b) b.setAttribute('aria-expanded', 'false');
      });
    });
  }

  function closeOtherPnCustomSelects(currentOuter) {
    document.querySelectorAll('.variant-wrapper--dropdown-custom.is-open').forEach(function (ow) {
      if (ow === currentOuter) return;
      ow.classList.remove('is-open');
      var ol = pnDdListEl(ow);
      var ob = pnDdTriggerEl(ow);
      if (ol) ol.hidden = true;
      if (ob) ob.setAttribute('aria-expanded', 'false');
    });
  }

  function initCustomVariantDropdowns(root) {
    bindPnCustomSelectOutsideClose();
    root.querySelectorAll('[data-pn-custom-select]').forEach(function (wrap) {
      var outer = wrap.closest('.variant-wrapper--dropdown-custom');
      var select = wrap.querySelector('select[data-variant-input]');
      var btn = wrap.querySelector('[data-pn-custom-select-trigger]');
      var list = wrap.querySelector('[data-pn-custom-select-list]');
      if (!select || !btn || !list) return;

      function setOpen(isOpen) {
        if (outer) outer.classList.toggle('is-open', isOpen);
        list.hidden = !isOpen;
        btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }

      function syncFromSelect() {
        var opt = select.options[select.selectedIndex];
        var label = opt ? opt.textContent.trim() : '';
        var valueEl = btn.querySelector('[data-pn-custom-select-value]');
        if (valueEl) valueEl.textContent = label;

        var items = list.querySelectorAll('.product-new__custom-select-option');
        Array.prototype.forEach.call(select.options, function (optEl, i) {
          var li = items[i];
          if (!li) return;
          var disabled = optEl.disabled;
          li.classList.toggle('is-disabled', disabled);
          li.setAttribute('aria-disabled', disabled ? 'true' : 'false');
          li.setAttribute('aria-selected', optEl.selected ? 'true' : 'false');
          li.classList.toggle('is-selected', optEl.selected);
        });
      }

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var opening = list.hidden;
        if (opening && outer) closeOtherPnCustomSelects(outer);
        setOpen(opening);
      });

      list.addEventListener('click', function (e) {
        var li = e.target.closest('.product-new__custom-select-option');
        if (!li || li.classList.contains('is-disabled')) return;
        var items = list.querySelectorAll('.product-new__custom-select-option');
        var idx = Array.prototype.indexOf.call(items, li);
        if (idx < 0 || !select.options[idx] || select.options[idx].disabled) return;
        if (select.selectedIndex !== idx) {
          select.selectedIndex = idx;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        setOpen(false);
        syncFromSelect();
      });

      select.addEventListener('change', syncFromSelect);
      root.addEventListener('variantChange', function () {
        window.requestAnimationFrame(syncFromSelect);
      });

      btn.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && outer && outer.classList.contains('is-open')) {
          setOpen(false);
        }
      });

      syncFromSelect();
    });
  }

  /**
   * Recharge's Subscriptions Widget 2.0 (loaded via the recharge-subscriptions
   * theme app embed) Vue-mounts a `.rc-container-wrapper` near the product form
   * on every product page. On the new template our custom Subscribe & Save
   * widget replaces it. The CSS rules in product-new.css.liquid handle visual
   * hiding (display: none !important, scoped to body:has([data-pn-root])); this
   * JS additionally tears down the Vue app and removes the node from the DOM
   * via bounded retries (no MutationObserver, to avoid thrashing if Vue keeps
   * re-mounting). All scoped to the new template by being called only from
   * initSection (which only fires for [data-pn-root] elements).
   */
  function initSuppressRechargeWidget(root) {
    var SELECTOR =
      '.rc-container-wrapper, .rc_container_wrapper, [data-widget-container-wrapper], .rc-widget, .rc_widget';

    function sweep() {
      var nodes = document.querySelectorAll(SELECTOR);
      for (var i = 0; i < nodes.length; i++) {
        var n = nodes[i];
        if (n && n.parentNode) n.parentNode.removeChild(n);
      }
      var form = root.querySelector('form.product-single__form');
      if (form) {
        var stray = form.querySelectorAll('input[name="selling_plan"]');
        for (var j = 0; j < stray.length; j++) {
          var inp = stray[j];
          if (inp.hasAttribute('data-pn-sw-native')) continue;
          if (inp.hasAttribute('data-pn-selling-plan')) continue;
          var sel = inp.closest('[data-pn-sw-native]');
          if (sel) continue;
          if (inp.parentNode) inp.parentNode.removeChild(inp);
        }
      }
    }

    function tryDestroy() {
      try {
        if (window.rechargeWidget && typeof window.rechargeWidget.destroy === 'function') {
          window.rechargeWidget.destroy();
        }
      } catch (e) {
        /* ignore */
      }
    }

    tryDestroy();
    sweep();

    if (window.__pnRechargeSuppressorScheduled) return;
    window.__pnRechargeSuppressorScheduled = true;

    [100, 500, 1500, 3000].forEach(function (ms) {
      window.setTimeout(function () {
        tryDestroy();
        sweep();
      }, ms);
    });
  }

  /**
   * Shopify payment/subscription terms render inside <shopify-payment-terms> shadow DOM,
   * so wrapper font-size does not apply. Inject fine-print styles once the component mounts.
   */
  function initPaymentTermsFinePrint(root) {
    var STYLE_MARK = 'data-pn-payment-terms-fine-print';
    var STYLE_TEXT =
      ':host{display:block!important;color:#6b6b6b!important;-webkit-text-size-adjust:none!important;text-size-adjust:none!important;}' +
      '#shopify-installments,#shopify-installments *,#shopify-installments-content,#shopify-installments-cta{' +
      'font-size:6px!important;line-height:1.15!important;letter-spacing:0!important;}';

    function apply(el) {
      if (!el || !el.shadowRoot) return;
      if (el.shadowRoot.querySelector('style[' + STYLE_MARK + ']')) return;
      var style = document.createElement('style');
      style.setAttribute(STYLE_MARK, '');
      style.textContent = STYLE_TEXT;
      el.shadowRoot.appendChild(style);
    }

    function sweep() {
      root.querySelectorAll('.product-new__payment-terms-below shopify-payment-terms').forEach(apply);
    }

    sweep();
    [100, 300, 800, 2000, 4000].forEach(function (ms) {
      window.setTimeout(sweep, ms);
    });

    if (window.customElements && typeof window.customElements.whenDefined === 'function') {
      window.customElements.whenDefined('shopify-payment-terms').then(sweep).catch(function () {});
    }

    try {
      var observer = new MutationObserver(sweep);
      observer.observe(root, { childList: true, subtree: true });
    } catch (e) {
      /* ignore */
    }
  }

  /**
   * Afterpay mounts via shopify-afterpay-javascript.js. If the selector was not set for this
   * template suffix, the widget can appear near the price — move it into #product-afterpay-placement.
   */
  function initAfterpayPlacement(root) {
    var anchor = root.querySelector('#product-afterpay-placement');
    if (!anchor) return;

    var PLACEMENT_SEL =
      'square-placement, afterpay-placement, .afterpay-paragraph, [data-afterpay-product-page], p.afterpay-text';

    function relocate() {
      root.querySelectorAll(PLACEMENT_SEL).forEach(function (el) {
        if (!el || el === anchor || anchor.contains(el)) return;
        anchor.appendChild(el);
      });
    }

    relocate();
    [200, 800, 2000, 4000].forEach(function (ms) {
      window.setTimeout(relocate, ms);
    });

    try {
      new MutationObserver(relocate).observe(root, { childList: true, subtree: true });
    } catch (e) {
      /* ignore */
    }
  }

  function initSection(root) {
    if (!root || root.dataset.pnInit === '1') return;
    root.dataset.pnInit = '1';
    initSuppressRechargeWidget(root);
    initGallery(root);
    initJumpLinks(root);
    initHeroRatingScroll(root);
    initCustomVariantDropdowns(root);
    initSubscribeSavePromo(root);
    initSubscribeWidget(root);
    initHiddenSellingPlanFields(root);
    initSellingPlanOnAddToCart(root);
    if (root.getAttribute('data-pn-force-selling-plan') === 'true') {
      pnEnsureSellingPlanBeforeAdd(root);
    }
    initMorePaymentOptionsProxy(root);
    initAfterpayPlacement(root);
    initPaymentTermsFinePrint(root);
    initStickyATC(root);
    initAddToCartSubtotal(root);
  }

  /**
   * Judge.me prints review dates as MM/DD/YYYY; rewrite to long form (e.g. April 22, 2026)
   * inside .product-new__reviews only. Watches DOM for pagination / async widget paint.
   */
  function initJudgeMeReviewLongDates(scope) {
    var root = scope && scope.nodeType === 1 ? scope : document;
    var sections = root.querySelectorAll('.product-new__reviews');
    if (!sections.length) return;

    var longFmt = new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });

    function formatOne(el) {
      if (!el || el.getAttribute('data-pn-long-date') === '1') return;
      var raw = (el.textContent || '').trim();
      var m = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (!m) return;
      var month = parseInt(m[1], 10);
      var day = parseInt(m[2], 10);
      var year = parseInt(m[3], 10);
      if (month < 1 || month > 12 || day < 1 || day > 31) return;
      var d = new Date(year, month - 1, day);
      if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return;
      el.textContent = longFmt.format(d);
      el.setAttribute('data-pn-long-date', '1');
    }

    function runInSection(section) {
      section.querySelectorAll('.jdgm-rev__timestamp').forEach(formatOne);
    }

    sections.forEach(function (section) {
      if (section.getAttribute('data-pn-jdgm-dates-obs') === '1') {
        runInSection(section);
        return;
      }
      section.setAttribute('data-pn-jdgm-dates-obs', '1');
      runInSection(section);
      var obs = new MutationObserver(function () {
        runInSection(section);
      });
      obs.observe(section, { childList: true, subtree: true });
    });
  }

  /**
   * Judge.me pagination / sort / histogram filters reflow async and call scrollIntoView, which
   * jumps the page and hides the theme “Customer reviews” heading. We suppress scrollIntoView
   * inside the section for a short window, re-apply scroll in rAF, and extend the window when
   * review cards are replaced in the DOM.
   */
  function initJudgeMeReviewsScrollPreserve(scope) {
    var root = scope && scope.nodeType === 1 ? scope : document;
    var sections = root.querySelectorAll('.product-new__reviews');
    if (!sections.length) return;

    sections.forEach(function (section) {
      if (section.getAttribute('data-pn-jdgm-scroll-preserve') === '1') return;
      section.setAttribute('data-pn-jdgm-scroll-preserve', '1');

      var savedX = 0;
      var savedY = 0;
      var rafId = 0;
      var heightReleaseTimer = null;

      function tick() {
        if (pnJdgmScrollLock.root === section && Date.now() < pnJdgmScrollLock.until) {
          window.scrollTo(savedX, savedY);
          rafId = window.requestAnimationFrame(tick);
        } else {
          rafId = 0;
        }
      }

      function scheduleHeightRelease() {
        clearTimeout(heightReleaseTimer);
        heightReleaseTimer = setTimeout(function () {
          pnReleaseJdgmReviewListHeight(section);
        }, 500);
      }

      function beginPreserve() {
        pnLockJdgmReviewListHeight(section);
        savedX = window.scrollX;
        savedY = window.scrollY;
        pnBumpJdgmScrollLock(section, 2400);
        if (!rafId) rafId = window.requestAnimationFrame(tick);
        clearTimeout(section._pnJdgmHeightFallback);
        section._pnJdgmHeightFallback = setTimeout(function () {
          pnReleaseJdgmReviewListHeight(section);
        }, 3200);
      }

      function maybeBeginFromTarget(t) {
        if (!t || !t.closest) return;
        if (
          t.closest('.jdgm-paginate') ||
          t.closest('.jdgm-rev-widg__sort-wrapper') ||
          t.closest('.jdgm-histogram')
        ) {
          beginPreserve();
        }
      }

      section.addEventListener(
        'pointerdown',
        function (e) {
          maybeBeginFromTarget(e.target);
        },
        true
      );

      section.addEventListener(
        'click',
        function (e) {
          maybeBeginFromTarget(e.target);
        },
        true
      );

      section.addEventListener(
        'change',
        function (e) {
          var t = e.target;
          if (t && t.closest && t.closest('.jdgm-rev-widg__sort-wrapper')) {
            beginPreserve();
          }
        },
        true
      );

      function attachListObserver(bodyEl) {
        if (!bodyEl || bodyEl.getAttribute('data-pn-jdgm-scroll-mo') === '1') return;
        bodyEl.setAttribute('data-pn-jdgm-scroll-mo', '1');
        var mo = new MutationObserver(function (mutations) {
          if (pnJdgmScrollLock.root !== section || Date.now() >= pnJdgmScrollLock.until) return;
          var listChange = false;
          for (var i = 0; i < mutations.length; i++) {
            if (mutations[i].type !== 'childList') continue;
            var nodes = mutations[i].addedNodes;
            for (var j = 0; j < nodes.length; j++) {
              var n = nodes[j];
              if (n.nodeType !== 1) continue;
              if (n.classList && n.classList.contains('jdgm-rev')) {
                listChange = true;
                break;
              }
              if (n.querySelector && n.querySelector('.jdgm-rev')) {
                listChange = true;
                break;
              }
            }
            if (listChange) break;
          }
          if (listChange) {
            pnBumpJdgmScrollLock(section, 800);
            window.scrollTo(savedX, savedY);
            if (!rafId) rafId = window.requestAnimationFrame(tick);
            scheduleHeightRelease();
          }
        });
        mo.observe(bodyEl, { childList: true, subtree: true });
      }

      var body = section.querySelector('.jdgm-rev-widg__body');
      attachListObserver(body);
      if (!body) {
        var tries = 0;
        var waitId = setInterval(function () {
          tries++;
          var b = section.querySelector('.jdgm-rev-widg__body');
          if (b) {
            clearInterval(waitId);
            attachListObserver(b);
          } else if (tries > 40) {
            clearInterval(waitId);
          }
        }, 250);
      }
    });
  }

  /**
   * Hide reviews section / buy-box rating when Judge.me reports zero reviews after async paint
   * (fallback when Shopify review metafields are not yet synced).
   */
  function pnJudgeMeReviewCount(el) {
    if (!el) return null;
    var n = el.getAttribute('data-number-of-reviews');
    if (n == null || n === '') return null;
    var count = parseInt(n, 10);
    return isNaN(count) ? null : count;
  }

  function pnHideShopifySection(el) {
    if (!el) return;
    var wrap = el.closest('.shopify-section');
    if (wrap) wrap.setAttribute('hidden', 'hidden');
  }

  function initJudgeMeHideEmptyReviews(scope) {
    var root = scope && scope.nodeType === 1 ? scope : document;

    root.querySelectorAll('[data-pn-reviews-section-empty]').forEach(pnHideShopifySection);

    function applyZeroReviews(pnRoot) {
      var anchor = (pnRoot && pnRoot.getAttribute('data-pn-reviews-anchor')) || 'reviews';
      root.querySelectorAll('[data-pn-reviews-section]').forEach(function (section) {
        section.setAttribute('hidden', 'hidden');
        pnHideShopifySection(section);
      });
      root.querySelectorAll('[data-pn-rating]').forEach(function (rating) {
        rating.setAttribute('hidden', 'hidden');
      });
      var jumplinkScope = pnRoot || root;
      jumplinkScope.querySelectorAll('[data-pn-jumplink]').forEach(function (btn) {
        if (btn.getAttribute('data-pn-jumplink') === anchor) {
          btn.setAttribute('hidden', 'hidden');
        }
      });
    }

    function checkBadge(badge, pnRoot) {
      var count = pnJudgeMeReviewCount(badge);
      if (count !== 0) return;
      var rootEl = pnRoot || badge.closest('[data-pn-root]');
      if (!rootEl || rootEl.getAttribute('data-pn-hide-empty-reviews') !== 'true') return;
      applyZeroReviews(rootEl);
    }

    function checkReviewsSection(section) {
      var pnRoot = section.closest('[data-pn-root]');
      if (pnRoot && pnRoot.getAttribute('data-pn-hide-empty-reviews') !== 'true') return;

      var badge = section.querySelector('.jdgm-prev-badge');
      if (badge) {
        checkBadge(badge, pnRoot);
        return;
      }

      var widg = section.querySelector('.jdgm-rev-widg');
      if (!widg) return;

      var revCount = section.querySelectorAll('.jdgm-rev').length;
      if (revCount === 0) {
        applyZeroReviews(pnRoot);
      }
    }

    function scanBadges() {
      root.querySelectorAll('.jdgm-prev-badge').forEach(function (badge) {
        checkBadge(badge);
      });
      root.querySelectorAll('[data-pn-reviews-section]').forEach(checkReviewsSection);
    }

    scanBadges();

    if (
      !root.querySelector('[data-pn-hide-empty-reviews]') &&
      !root.querySelector('[data-pn-reviews-section]') &&
      !root.querySelector('[data-pn-rating]') &&
      !root.querySelector('[data-pn-reviews-section-empty]')
    ) {
      return;
    }

    var obs = new MutationObserver(scanBadges);
    obs.observe(root, { childList: true, subtree: true });

    [400, 1200, 3000].forEach(function (ms) {
      setTimeout(scanBadges, ms);
    });
  }

  ready(function () {
    document.querySelectorAll('[data-pn-root]').forEach(initSection);
    initJudgeMeReviewLongDates(document);
    initJudgeMeReviewsScrollPreserve(document);
    initJudgeMeHideEmptyReviews(document);
    initPdpFaqAccordions(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-pn-root]');
    if (root) initSection(root);
    initJudgeMeReviewLongDates(event.target);
    initJudgeMeReviewsScrollPreserve(event.target);
    initJudgeMeHideEmptyReviews(event.target);
    initPdpFaqAccordions(event.target);
  });
})();
